import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

export default function Header({ user, logo, scrollToSection, sticky = true }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);

  // close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const goTop = () => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      window.scrollTo(0, 0);
    }
  };

  const goSection = (id) => {
    setMobileOpen(false);
    if (location.pathname === "/" && typeof scrollToSection === "function") {
      scrollToSection(id);
      return;
    }
    navigate({ pathname: "/", hash: `#${id}` });
  };

  return (
    <header className={`${sticky ? "sticky top-0" : "relative"} z-50 reveal reveal-up`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
      <Link
        to="/"
        className="group flex items-center gap-2 reveal reveal-left delay-1 transition-transform duration-300 hover:-translate-y-[1px]"
      >
        <img
          src={logo}
          alt="MyRDP Cloud Logo"
          className="h-8 w-auto transition-transform duration-300 group-hover:scale-[1.05]"
        />

        <span className="relative text-xl font-montserrat font-medium tracking-wider text-white">
          MyRDP
          {/* underline sweep */}
          <span className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-0 bg-white/70 transition-all duration-300 group-hover:w-full" />
        </span>

        {/* subtle glow */}
        <span className="pointer-events-none absolute inset-0 -z-10 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-40 bg-[#0B3BF0]/30 rounded-full" />
      </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center text-[#E7E7E7] bg-[#0D0C0F66] gap-6 text-sm md:flex border border-[#FFFFFF1A] py-4 px-5 rounded-full backdrop-blur-[60px] reveal reveal-up delay-2">
          <button type="button" onClick={goTop} className="hover:text-white reveal reveal-up delay-2">
            Home
          </button>
          <button type="button" onClick={() => goSection("pricing")} className="hover:text-white reveal reveal-up delay-3">
            Pricing
          </button>
          <button type="button" onClick={() => goSection("features")} className="hover:text-white reveal reveal-up delay-4">
            Features
          </button>
          <Link to="/blog" className="hover:text-white reveal reveal-up delay-4">
            Blog
          </Link>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3 reveal reveal-right delay-2">
          {/* Desktop CTA */}
          {user ? (
            <Link
              to="/dashboard"
              className="hidden md:inline-flex rounded-full border border-blue-400/45 px-4 py-2 text-sm text-blue-100 hover:bg-blue-400/10"
            >
              Dashboard
            </Link>
          ) : (
            <div className="hidden items-center gap-2 md:flex reveal reveal-zoom delay-2">
              <Link
                to="/signup"
                className="inline-flex rounded-full border border-white/40 px-5 py-3 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10"
              >
                Sign up
              </Link>
              <Link
                to="/signin"
                className="group relative inline-flex overflow-hidden bg-white px-5 py-3 rounded-full text-sm font-medium text-black transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/95 hover:shadow-[0_12px_28px_rgba(255,255,255,0.15)]"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-[120%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-black/10 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[120%]" />
                <span className="relative z-10">Sign in</span>
              </Link>
            </div>
          )}

          {/* Hamburger (mobile) */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-[20px] p-3 text-white reveal reveal-zoom delay-3"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 7H20M4 12H20M4 17H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden px-4 pb-4 reveal reveal-up">
          <div className="rounded-2xl border border-white/10 bg-[#0D0C0Fcc] backdrop-blur-[60px] p-3 reveal reveal-zoom">
            <button
              onClick={goTop}
              className="w-full text-left rounded-xl px-3 py-3 text-sm text-[#E7E7E7] hover:bg-white/5 hover:text-white reveal reveal-up delay-1"
            >
              Home
            </button>
            <button
              onClick={() => goSection("pricing")}
              className="w-full text-left rounded-xl px-3 py-3 text-sm text-[#E7E7E7] hover:bg-white/5 hover:text-white reveal reveal-up delay-2"
            >
              Pricing
            </button>
            <button
              onClick={() => goSection("features")}
              className="mt-1 w-full text-left rounded-xl px-3 py-3 text-sm text-[#E7E7E7] hover:bg-white/5 hover:text-white reveal reveal-up delay-3"
            >
              Features
            </button>
            <Link
              to="/blog"
              onClick={() => setMobileOpen(false)}
              className="mt-1 block w-full rounded-xl px-3 py-3 text-sm text-[#E7E7E7] hover:bg-white/5 hover:text-white reveal reveal-up delay-3"
            >
              Blog
            </Link>

            <div className="mt-3 pt-3 border-t border-white/10 reveal reveal-up delay-4">
              {user ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full text-center rounded-xl border border-blue-400/45 px-4 py-3 text-sm text-blue-100 hover:bg-blue-400/10 reveal reveal-zoom delay-4"
                >
                  Dashboard
                </Link>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center rounded-xl border border-white/40 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 reveal reveal-zoom delay-4"
                  >
                    Sign up
                  </Link>
                  <Link
                    to="/signin"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center rounded-xl bg-white px-4 py-3 text-sm font-semibold text-black hover:bg-white/90 reveal reveal-zoom delay-4"
                  >
                    Sign in
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}